/**
 * TopBar — Panel navigation for the web editor with drop zones.
 * Matches Tauri TopBarEnhanced layout parity with animated drop zones.
 */
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import {
  CalendarDays,
  Database,
  Image,
  Settings,
  Sparkles,
  LayoutGrid,
  Search,
  Plus,
  Check,
  Download,
} from 'lucide-react';
import { MessagesDropdown } from '@/components/MessagesDropdown';
import { OrgSwitcher } from '@/components/OrgSwitcher';
import { TeamSwitcher } from '@/components/TeamSwitcher';
import { ProfileMenu } from '@/components/ProfileMenu';
import { ExportModal } from '@/components/ExportModal';
import { useAppStore } from '@/store/appStore';
import { useCanvasStore } from '@/store/canvasStore';
import { useState, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import type { Id, Doc } from '../../../convex/_generated/dataModel';

const navItems = [
  { id: 'canvas' as const, icon: LayoutGrid, label: 'Canvas' },
  { id: 'events' as const, icon: CalendarDays, label: 'Events' },
  { id: 'entities' as const, icon: Database, label: 'Entities' },
  { id: 'assets' as const, icon: Image, label: 'Assets' },
  { id: 'settings' as const, icon: Settings, label: 'Settings' },
];

export function TopBar() {
  const [showExportModal, setShowExportModal] = useState(false);
  const { 
    activePanel, 
    setActivePanel, 
    setShowGenerationModal, 
    setShowSearch, 
    setShowWelcomeModal, 
    currentProjectId,
  } = useAppStore();
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const project = useQuery(
    api.projects.get,
    currentProjectId ? { accountId, id: currentProjectId } : 'skip'
  );
  
  // Drop zone state
  const [isDragOverCanvas, setIsDragOverCanvas] = useState(false);
  const [dropFeedback, setDropFeedback] = useState<string | null>(null);
  const { addLayer } = useCanvasStore();
  const assets = useQuery(api.assets.list, { accountId });

  // Handle global drag events for custom drag from AssetManager
  useEffect(() => {
    const handleDragStart = (e: DragEvent) => {
      const assetId = e.dataTransfer?.getData('application/asset-id');
      if (assetId) {
        // Store for cross-component access
        (window as any).__draggedAssetId = assetId;
      }
    };
    
    window.addEventListener('dragstart', handleDragStart);
    return () => window.removeEventListener('dragstart', handleDragStart);
  }, []);

  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOverCanvas(true);
  }, []);

  const handleCanvasDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverCanvas(false);
  }, []);

  const handleCanvasDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOverCanvas(false);

    const assetId = e.dataTransfer.getData('application/asset-id') || (window as any).__draggedAssetId;
    if (!assetId) return;

    const asset = assets?.find((a: Doc<'assets'>) => a._id === assetId);
    if (!asset) return;

    // Switch to canvas panel
    setActivePanel('canvas');
    
    // Add asset as layer
    addLayer({
      type: 'image',
      name: asset.name,
      visible: true,
      locked: false,
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 200,
      width: asset.width ?? 300,
      height: asset.height ?? 300,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      opacity: 1,
      asset_id: assetId,
    });

    setDropFeedback(`Added ${asset.name} to canvas`);
    setTimeout(() => setDropFeedback(null), 3000);
    
    // Clear stored drag data
    (window as any).__draggedAssetId = null;
  }, [addLayer, assets, setActivePanel]);

  return (
    <>
      <header className="h-14 flex-shrink-0 border-b border-border bg-card/80 backdrop-blur-sm flex items-center justify-between px-4">
        <div className="flex items-center gap-1 min-w-0">
          <Link
            to="/"
            className="flex items-center gap-2 mr-3 text-muted-foreground hover:text-foreground transition-colors group"
          >
            <img src="/eventtools-logo.svg" alt="" className="h-6 w-6 group-hover:scale-110 transition-transform" />
            <span className="font-semibold text-sm hidden md:inline">Event.Tools</span>
          </Link>
          {project && (
            <span className="hidden sm:inline text-xs text-muted-foreground truncate max-w-[140px] ml-1 px-2 py-0.5 rounded bg-muted/50" title={project.name}>
              {project.name}
            </span>
          )}
          <div className="h-5 w-px bg-border mx-2 hidden sm:block" />
          <nav className="flex items-center gap-0.5 rounded-lg bg-muted/30 p-0.5">
            {navItems.map(({ id, icon: Icon, label }) => (
              <TopBarButton
                key={id}
                id={id}
                icon={Icon}
                label={label}
                isActive={activePanel === id}
                onClick={() => setActivePanel(id)}
                isDropTarget={id === 'canvas'}
                isDragOver={id === 'canvas' && isDragOverCanvas}
                onDragOver={id === 'canvas' ? handleCanvasDragOver : undefined}
                onDragLeave={id === 'canvas' ? handleCanvasDragLeave : undefined}
                onDrop={id === 'canvas' ? handleCanvasDrop : undefined}
              />
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-1.5">
          <OrgSwitcher />
          <TeamSwitcher />
          <button onClick={() => setShowSearch(true)} className="rounded-lg p-2 text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-all" title="Search (Ctrl+K)">
            <Search className="h-4 w-4" />
          </button>
          <button onClick={() => setShowWelcomeModal(true)} className="rounded-lg p-2 text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-all text-xs font-medium" title="Welcome">
            ?
          </button>
          <MessagesDropdown />
          <div className="h-5 w-px bg-border mx-0.5" />
          <button
            onClick={() => setShowGenerationModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all hover:shadow-[0_0_12px_hsl(168_63%_39%/0.3)] active:scale-[0.98]"
          >
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Generate</span>
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-muted hover:border-muted-foreground/20 transition-all active:scale-[0.98]"
            title="Export flyer"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <ProfileMenu />
        </div>
      </header>

      {/* Drop feedback toast */}
      {dropFeedback && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className="bg-card border border-primary/20 rounded-lg shadow-lg shadow-primary/5 px-4 py-3 flex items-center gap-2">
            <div className="h-5 w-5 rounded-full bg-green-500/15 flex items-center justify-center">
              <Check className="h-3 w-3 text-green-500" />
            </div>
            <span className="text-sm">{dropFeedback}</span>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} />
    </>
  );
}

interface TopBarButtonProps {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  isActive: boolean;
  onClick: () => void;
  isDropTarget?: boolean;
  isDragOver?: boolean;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}

function TopBarButton({
  icon: Icon,
  label,
  isActive,
  onClick,
  isDropTarget,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
}: TopBarButtonProps) {
  return (
    <button
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 relative",
        isActive
          ? 'bg-primary/15 text-primary shadow-[inset_0_-2px_0_hsl(168_63%_39%)]'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
        isDropTarget && "cursor-copy",
        isDragOver && "bg-primary/20 ring-2 ring-primary/50 scale-105 shadow-[0_0_12px_hsl(168_63%_39%/0.2)]"
      )}
    >
      <Icon className={cn("h-4 w-4", isActive && "text-primary")} />
      <span className="hidden sm:inline">{label}</span>
      {isDragOver && (
        <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] bg-primary text-primary-foreground px-2 py-0.5 rounded-full whitespace-nowrap animate-fade-in shadow-lg z-10">
          Drop to add
        </span>
      )}
    </button>
  );
}
